import { useEffect, useState, useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { accountApi } from '../../api/account';
import { authApi } from '../../api/auth';
import { paymentsApi } from '../../api/cart';
import type { CheckoutResultDto, CustomerAddressUpsertDto } from '../../api/types';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  FormErrors,
  Modal,
  PageLoader,
  Spinner,
} from '../../components/ui';
import { ProductCard } from '../../components/ProductCard';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../context/AuthContext';
import { formatDate, formatDateTime, money, relativeTime } from '../../lib/format';
import { trackPurchase } from '../../lib/analytics';
import { TURKEY_CITIES, getDistrictsForCity } from '../../lib/turkeyLocations';
import { SearchableSelect } from '../../components/SearchableSelect';
import { shippingLocationsApi } from '../../api';

/* ---------------------------- Genel bakis ---------------------------- */

export function AccountOverviewPage() {
  const { user } = useAuth();

  const orders = useQuery({
    queryKey: ['account', 'orders', 'recent'],
    queryFn: () => accountApi.myOrders({ pageSize: 3 }),
  });

  const addresses = useQuery({
    queryKey: ['account', 'addresses'],
    queryFn: () => accountApi.addresses(),
  });

  const wishlist = useQuery({
    queryKey: ['account', 'wishlist'],
    queryFn: () => accountApi.wishlist(),
  });

  return (
    <div className="space-y-6">
      <section className="card p-5">
        <h2 className="text-sm font-semibold text-ink-900">Merhaba {user?.firstName ?? ''}</h2>
        <p className="mt-1 text-sm text-ink-600">{user?.email}</p>
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <span className="badge bg-ink-100 text-ink-700">
            Üyelik: {formatDate(user?.createdAt)}
          </span>
          {user?.customerGroupName ? (
            <span className="badge bg-brand-50 text-brand-800">{user.customerGroupName}</span>
          ) : null}
          <span
            className={`badge ${user?.emailConfirmed ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800'}`}
          >
            {user?.emailConfirmed ? 'E-posta doğrulandı' : 'E-posta doğrulanmadı'}
          </span>
          {user?.twoFactorEnabled ? (
            <span className="badge bg-emerald-50 text-emerald-700">İki adımlı doğrulama açık</span>
          ) : null}
        </div>
      </section>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Sipariş" value={orders.data?.totalCount ?? 0} to="/hesabim/siparisler" />
        <StatCard label="Kayıtlı adres" value={addresses.data?.length ?? 0} to="/hesabim/adresler" />
        <StatCard label="Favori ürün" value={wishlist.data?.length ?? 0} to="/hesabim/favoriler" />
      </div>

      <section className="card p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-ink-900">Son siparişler</h2>
          <Link to="/hesabim/siparisler" className="text-sm text-brand-700 hover:underline">
            Tümü →
          </Link>
        </div>

        {orders.isLoading ? (
          <p className="text-sm text-ink-500">Yükleniyor…</p>
        ) : orders.data && orders.data.items.length > 0 ? (
          <ul className="divide-y divide-ink-100">
            {orders.data.items.map((order) => (
              <li key={order.publicId} className="flex items-center justify-between gap-3 py-3">
                <div>
                  <Link
                    to={`/hesabim/siparisler/${order.publicId}`}
                    className="text-sm font-medium text-ink-900 hover:text-brand-700"
                  >
                    {order.orderNumber}
                  </Link>
                  <p className="text-xs text-ink-500">{relativeTime(order.createdAt)}</p>
                </div>
                <span className="text-sm font-medium text-ink-900">
                  {money(order.grandTotal, order.currencyCode)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-ink-500">Henüz siparişiniz yok.</p>
        )}
      </section>
    </div>
  );
}

function StatCard({ label, value, to }: { label: string; value: number; to: string }) {
  return (
    <Link to={to} className="card p-5 transition hover:shadow-pop">
      <p className="text-sm text-ink-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-ink-900">{value}</p>
    </Link>
  );
}

/* ------------------------------ Profil ------------------------------ */

export function ProfilePage() {
  const { user, refresh } = useAuth();
  const toast = useToast();

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    dateOfBirth: '',
    acceptsMarketing: false,
  });

  useEffect(() => {
    if (user) {
      setForm({
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        phoneNumber: user.phoneNumber ?? '',
        dateOfBirth: user.dateOfBirth ? user.dateOfBirth.slice(0, 10) : '',
        acceptsMarketing: user.acceptsMarketing,
      });
    }
  }, [user]);

  const save = useMutation({
    mutationFn: () =>
      authApi.updateProfile({
        firstName: form.firstName,
        lastName: form.lastName,
        phoneNumber: form.phoneNumber || null,
        dateOfBirth: form.dateOfBirth || null,
        acceptsMarketing: form.acceptsMarketing,
      }),
    onSuccess: async (message) => {
      toast.success(message ?? 'Profiliniz güncellendi.');
      await refresh();
    },
  });

  const exportData = useMutation({
    mutationFn: () => authApi.exportPersonalData(),
    onSuccess: (data) => {
      // KVKK disa aktarimi tarayicida dosya olarak indirilir.
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'kisisel-verilerim.json';
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Kişisel verileriniz indirildi.');
    },
    onError: (error) => toast.fromError(error, 'Veriler dışa aktarılamadı.'),
  });

  return (
    <div className="space-y-6">
      <section className="card p-5">
        <h2 className="mb-4 text-sm font-semibold text-ink-900">Profil bilgileri</h2>

        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <FormErrors error={save.error} />

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Ad" htmlFor="profile-first" required>
              <input
                id="profile-first"
                className="field"
                required
                value={form.firstName}
                onChange={(event) => setForm((c) => ({ ...c, firstName: event.target.value }))}
              />
            </Field>
            <Field label="Soyad" htmlFor="profile-last" required>
              <input
                id="profile-last"
                className="field"
                required
                value={form.lastName}
                onChange={(event) => setForm((c) => ({ ...c, lastName: event.target.value }))}
              />
            </Field>
            <Field label="Telefon" htmlFor="profile-phone">
              <input
                id="profile-phone"
                type="tel"
                className="field"
                value={form.phoneNumber}
                onChange={(event) => setForm((c) => ({ ...c, phoneNumber: event.target.value }))}
              />
            </Field>
            <Field label="Doğum tarihi" htmlFor="profile-birth">
              <input
                id="profile-birth"
                type="date"
                className="field"
                value={form.dateOfBirth}
                onChange={(event) => setForm((c) => ({ ...c, dateOfBirth: event.target.value }))}
              />
            </Field>
          </div>

          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-ink-300 text-brand-600"
              checked={form.acceptsMarketing}
              onChange={(event) => setForm((c) => ({ ...c, acceptsMarketing: event.target.checked }))}
            />
            Kampanya e-postaları almak istiyorum
          </label>

          <button type="submit" className="btn-primary" disabled={save.isPending}>
            {save.isPending ? <Spinner className="h-4 w-4" /> : null}
            Kaydet
          </button>
        </form>
      </section>

      <section className="card p-5">
        <h2 className="mb-2 text-sm font-semibold text-ink-900">Kişisel verileriniz</h2>
        <p className="mb-4 text-sm text-ink-600">
          KVKK kapsamında hesabınızda tutulan tüm verileri indirebilirsiniz.
        </p>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => exportData.mutate()}
          disabled={exportData.isPending}
        >
          {exportData.isPending ? <Spinner className="h-4 w-4" /> : null}
          Verilerimi indir
        </button>
      </section>
    </div>
  );
}

/* ------------------------------ Guvenlik ------------------------------ */

export function SecurityPage() {
  const { user, refresh, logout } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
    signOutOtherSessions: true,
  });
  const [emailForm, setEmailForm] = useState({ newEmail: '', currentPassword: '' });
  const [twoFactorPassword, setTwoFactorPassword] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');

  const sessions = useQuery({
    queryKey: ['auth', 'sessions'],
    queryFn: () => authApi.sessions(),
  });

  const changePassword = useMutation({
    mutationFn: () => authApi.changePassword(passwordForm),
    onSuccess: (message) => {
      toast.success(message ?? 'Parolanız değiştirildi.');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
        signOutOtherSessions: true,
      });
      void queryClient.invalidateQueries({ queryKey: ['auth', 'sessions'] });
    },
  });

  const changeEmail = useMutation({
    mutationFn: () => authApi.requestEmailChange(emailForm),
    onSuccess: (message) => {
      toast.success(message ?? 'Doğrulama e-postası gönderildi.');
      setEmailForm({ newEmail: '', currentPassword: '' });
    },
  });

  const toggleTwoFactor = useMutation({
    mutationFn: () =>
      user?.twoFactorEnabled
        ? authApi.disableTwoFactor({ currentPassword: twoFactorPassword })
        : authApi.enableTwoFactor({ currentPassword: twoFactorPassword }),
    onSuccess: async (message) => {
      toast.success(message ?? 'İki adımlı doğrulama ayarı güncellendi.');
      setTwoFactorPassword('');
      await refresh();
    },
  });

  const revokeSession = useMutation({
    mutationFn: (sessionId: number) => authApi.revokeSession(sessionId),
    onSuccess: () => {
      toast.success('Oturum sonlandırıldı.');
      void queryClient.invalidateQueries({ queryKey: ['auth', 'sessions'] });
    },
    onError: (error) => toast.fromError(error, 'Oturum sonlandırılamadı.'),
  });

  const logoutAll = useMutation({
    mutationFn: () => authApi.logoutAll(),
    onSuccess: () => {
      toast.success('Tüm oturumlar kapatıldı.');
      void logout();
    },
  });

  const deleteAccount = useMutation({
    mutationFn: () => authApi.deleteAccount({ currentPassword: deletePassword }),
    onSuccess: () => {
      toast.success('Hesabınız silindi.');
      void logout();
    },
    onError: (error) => toast.fromError(error, 'Hesap silinemedi.'),
  });

  return (
    <div className="space-y-6">
      {/* Parola */}
      <section className="card p-5">
        <h2 className="mb-4 text-sm font-semibold text-ink-900">Parola değiştir</h2>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            changePassword.mutate();
          }}
        >
          <FormErrors error={changePassword.error} />

          <Field label="Mevcut parola" htmlFor="cur-pass" required>
            <input
              id="cur-pass"
              type="password"
              autoComplete="current-password"
              required
              className="field"
              value={passwordForm.currentPassword}
              onChange={(event) =>
                setPasswordForm((c) => ({ ...c, currentPassword: event.target.value }))
              }
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Yeni parola"
              htmlFor="new-pass"
              required
              hint="Son 5 parolanız tekrar kullanılamaz."
            >
              <input
                id="new-pass"
                type="password"
                autoComplete="new-password"
                required
                className="field"
                value={passwordForm.newPassword}
                onChange={(event) =>
                  setPasswordForm((c) => ({ ...c, newPassword: event.target.value }))
                }
              />
            </Field>
            <Field label="Yeni parola tekrar" htmlFor="new-pass2" required>
              <input
                id="new-pass2"
                type="password"
                autoComplete="new-password"
                required
                className="field"
                value={passwordForm.confirmNewPassword}
                onChange={(event) =>
                  setPasswordForm((c) => ({ ...c, confirmNewPassword: event.target.value }))
                }
              />
            </Field>
          </div>

          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-ink-300 text-brand-600"
              checked={passwordForm.signOutOtherSessions}
              onChange={(event) =>
                setPasswordForm((c) => ({ ...c, signOutOtherSessions: event.target.checked }))
              }
            />
            Diğer cihazlardaki oturumları da kapat
          </label>

          <button type="submit" className="btn-primary" disabled={changePassword.isPending}>
            {changePassword.isPending ? <Spinner className="h-4 w-4" /> : null}
            Parolayı değiştir
          </button>
        </form>
      </section>

      {/* Iki adimli dogrulama */}
      <section className="card p-5">
        <h2 className="mb-2 text-sm font-semibold text-ink-900">İki adımlı doğrulama</h2>
        <p className="mb-4 text-sm text-ink-600">
          {user?.twoFactorEnabled
            ? 'Açık. Her girişte e-posta adresinize tek kullanımlık kod gönderilir.'
            : 'Kapalı. Açtığınızda her girişte e-postanıza kod gönderilir.'}
        </p>

        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            toggleTwoFactor.mutate();
          }}
        >
          <Field label="Parolanız" htmlFor="tfa-pass" className="min-w-56 flex-1">
            <input
              id="tfa-pass"
              type="password"
              required
              className="field"
              value={twoFactorPassword}
              onChange={(event) => setTwoFactorPassword(event.target.value)}
            />
          </Field>
          <button
            type="submit"
            className={user?.twoFactorEnabled ? 'btn-secondary' : 'btn-primary'}
            disabled={toggleTwoFactor.isPending}
          >
            {toggleTwoFactor.isPending ? <Spinner className="h-4 w-4" /> : null}
            {user?.twoFactorEnabled ? 'Kapat' : 'Aç'}
          </button>
        </form>
        <FormErrors error={toggleTwoFactor.error} />
      </section>

      {/* E-posta degisikligi */}
      <section className="card p-5">
        <h2 className="mb-2 text-sm font-semibold text-ink-900">E-posta adresi</h2>
        <p className="mb-4 text-sm text-ink-600">
          Şu anki adres: <strong>{user?.email}</strong>. Yeni adrese doğrulama bağlantısı gönderilir.
        </p>

        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            changeEmail.mutate();
          }}
        >
          <FormErrors error={changeEmail.error} />

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Yeni e-posta" htmlFor="new-email" required>
              <input
                id="new-email"
                type="email"
                required
                className="field"
                value={emailForm.newEmail}
                onChange={(event) => setEmailForm((c) => ({ ...c, newEmail: event.target.value }))}
              />
            </Field>
            <Field label="Parolanız" htmlFor="email-pass" required>
              <input
                id="email-pass"
                type="password"
                required
                className="field"
                value={emailForm.currentPassword}
                onChange={(event) =>
                  setEmailForm((c) => ({ ...c, currentPassword: event.target.value }))
                }
              />
            </Field>
          </div>

          <button type="submit" className="btn-secondary" disabled={changeEmail.isPending}>
            {changeEmail.isPending ? <Spinner className="h-4 w-4" /> : null}
            Doğrulama gönder
          </button>
        </form>
      </section>

      {/* Oturumlar */}
      <section className="card p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-ink-900">Açık oturumlar</h2>
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => logoutAll.mutate()}
            disabled={logoutAll.isPending}
          >
            Tüm oturumları kapat
          </button>
        </div>

        {sessions.isLoading ? (
          <p className="text-sm text-ink-500">Yükleniyor…</p>
        ) : sessions.data && sessions.data.length > 0 ? (
          <ul className="divide-y divide-ink-100">
            {sessions.data.map((session) => (
              <li key={session.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink-900">
                    {session.deviceName ?? 'Bilinmeyen cihaz'}
                    {session.isCurrent ? (
                      <span className="badge ml-2 bg-emerald-50 text-emerald-700">Bu cihaz</span>
                    ) : null}
                  </p>
                  <p className="text-xs text-ink-500">
                    {session.ipAddress ?? '—'}
                    {session.location ? ` · ${session.location}` : ''} · son kullanım{' '}
                    {relativeTime(session.lastUsedAt ?? session.createdAt)}
                  </p>
                </div>
                {!session.isCurrent ? (
                  <button
                    type="button"
                    className="btn-ghost btn-sm text-red-600"
                    onClick={() => revokeSession.mutate(session.id)}
                    disabled={revokeSession.isPending}
                  >
                    Sonlandır
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-ink-500">Açık oturum bulunamadı.</p>
        )}
      </section>

      {/* Hesap silme */}
      <section className="card border-red-200 p-5">
        <h2 className="mb-2 text-sm font-semibold text-red-800">Hesabı sil</h2>
        <p className="mb-4 text-sm text-ink-600">
          Hesabınız kapatılır ve kişisel verileriniz silinir. Bu işlem geri alınamaz.
        </p>
        <button type="button" className="btn-danger" onClick={() => setDeleteOpen(true)}>
          Hesabımı sil
        </button>
      </section>

      <Modal
        open={deleteOpen}
        title="Hesabı sil"
        description="Devam etmek için parolanızı girin."
        onClose={() => setDeleteOpen(false)}
        size="sm"
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setDeleteOpen(false)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-danger"
              disabled={!deletePassword || deleteAccount.isPending}
              onClick={() => deleteAccount.mutate()}
            >
              {deleteAccount.isPending ? <Spinner className="h-4 w-4" /> : null}
              Hesabımı sil
            </button>
          </>
        }
      >
        <Field label="Parolanız" htmlFor="delete-pass" required>
          <input
            id="delete-pass"
            type="password"
            className="field"
            value={deletePassword}
            onChange={(event) => setDeletePassword(event.target.value)}
          />
        </Field>
      </Modal>
    </div>
  );
}

/* ------------------------------ Adresler ------------------------------ */

const emptyAddress: CustomerAddressUpsertDto = {
  title: '',
  firstName: '',
  lastName: '',
  phone: '',
  addressLine1: '',
  district: '',
  city: '',
  countryCode: 'TR',
  isCorporate: false,
  isDefaultShipping: false,
  isDefaultBilling: false,
};

export function AddressesPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<{ id: number | null; form: CustomerAddressUpsertDto } | null>(
    null,
  );
  const [deleteId, setDeleteId] = useState<number | null>(null);

  // 1. Fetch cities from unified backend API (with fallback to TURKEY_CITIES)
  const { data: apiCities } = useQuery({
    queryKey: ['shipping', 'cities'],
    queryFn: shippingLocationsApi.getCities,
    staleTime: 1000 * 60 * 60,
  });
  const cities = useMemo(() => {
    if (apiCities && apiCities.length > 0) return apiCities;
    return TURKEY_CITIES;
  }, [apiCities]);

  const selectedCityObj = useMemo(() => {
    return cities.find((c) => c.name === editing?.form.city);
  }, [cities, editing?.form.city]);

  // 2. Fetch districts from unified backend API for selected city
  const { data: apiDistricts } = useQuery({
    queryKey: ['shipping', 'districts', selectedCityObj?.id],
    queryFn: () => shippingLocationsApi.getDistricts(selectedCityObj!.id),
    enabled: !!selectedCityObj?.id,
    staleTime: 1000 * 60 * 60,
  });
  const districts = useMemo(() => {
    if (apiDistricts && apiDistricts.length > 0) {
      return apiDistricts.map((d) => d.name);
    }
    return getDistrictsForCity(editing?.form.city ?? '');
  }, [apiDistricts, editing?.form.city]);

  const [neighborhoods, setNeighborhoods] = useState<{ value: string; label: string }[]>([]);
  const [loadingNeighborhoods, setLoadingNeighborhoods] = useState(false);

  useEffect(() => {
    if (!selectedCityObj || !editing?.form.district) {
      setNeighborhoods([]);
      return;
    }

    let isMounted = true;
    setLoadingNeighborhoods(true);
    shippingLocationsApi
      .getNeighborhoods(selectedCityObj.id, editing.form.district)
      .then((items) => {
        if (!isMounted) return;
        setNeighborhoods(items.map((n) => ({ value: n.name, label: n.fullName || n.name })));
      })
      .catch(() => {
        if (!isMounted) return;
        setNeighborhoods([]);
      })
      .finally(() => {
        if (isMounted) setLoadingNeighborhoods(false);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedCityObj?.id, editing?.form.district]);

  const addresses = useQuery({
    queryKey: ['account', 'addresses'],
    queryFn: () => accountApi.addresses(),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['account', 'addresses'] });

  const save = useMutation({
    mutationFn: () =>
      editing!.id === null
        ? accountApi.createAddress(editing!.form)
        : accountApi.updateAddress(editing!.id, editing!.form),
    onSuccess: () => {
      toast.success('Adres kaydedildi.');
      setEditing(null);
      void invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => accountApi.deleteAddress(id),
    onSuccess: () => {
      toast.success('Adres silindi.');
      setDeleteId(null);
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'Adres silinemedi.'),
  });

  const setDefault = useMutation({
    mutationFn: ({ id, shipping }: { id: number; shipping: boolean }) =>
      accountApi.setDefaultAddress(id, shipping),
    onSuccess: () => {
      toast.success('Varsayılan adres güncellendi.');
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'İşlem tamamlanamadı.'),
  });

  if (addresses.isLoading) return <PageLoader />;
  if (addresses.isError)
    return <ErrorState error={addresses.error} onRetry={() => void addresses.refetch()} />;

  const data = addresses.data!;

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button
          type="button"
          className="btn-primary btn-sm"
          onClick={() => setEditing({ id: null, form: emptyAddress })}
        >
          Yeni adres ekle
        </button>
      </div>

      {data.length === 0 ? (
        <EmptyState
          title="Kayıtlı adresiniz yok"
          description="Ödeme adımını hızlandırmak için adres ekleyebilirsiniz."
        />
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2">
          {data.map((address) => (
            <li key={address.id} className="card p-5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-ink-900">
                    {address.title ?? `${address.firstName} ${address.lastName}`}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {address.isDefaultShipping ? (
                      <span className="badge bg-brand-50 text-brand-800">Varsayılan teslimat</span>
                    ) : null}
                    {address.isDefaultBilling ? (
                      <span className="badge bg-ink-100 text-ink-700">Varsayılan fatura</span>
                    ) : null}
                    {address.isCorporate ? (
                      <span className="badge bg-ink-100 text-ink-700">Kurumsal</span>
                    ) : null}
                  </div>
                </div>
              </div>

              <address className="mt-3 text-sm not-italic leading-6 text-ink-600">
                {address.firstName} {address.lastName}
                <br />
                {address.addressLine1}
                {address.addressLine2 ? (
                  <>
                    <br />
                    {address.addressLine2}
                  </>
                ) : null}
                <br />
                {address.district} / {address.city}
                {address.postalCode ? ` ${address.postalCode}` : ''}
                <br />
                {address.countryName} · {address.phone}
              </address>

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() =>
                    setEditing({
                      id: address.id,
                      form: {
                        title: address.title,
                        firstName: address.firstName,
                        lastName: address.lastName,
                        company: address.company,
                        phone: address.phone,
                        addressLine1: address.addressLine1,
                        addressLine2: address.addressLine2,
                        district: address.district,
                        city: address.city,
                        postalCode: address.postalCode,
                        countryCode: address.countryCode,
                        deliveryNote: address.deliveryNote,
                        isCorporate: address.isCorporate,
                        taxOffice: address.taxOffice,
                        taxNumber: address.taxNumber,
                        isDefaultShipping: address.isDefaultShipping,
                        isDefaultBilling: address.isDefaultBilling,
                      },
                    })
                  }
                >
                  Düzenle
                </button>

                {!address.isDefaultShipping ? (
                  <button
                    type="button"
                    className="btn-ghost btn-sm"
                    onClick={() => setDefault.mutate({ id: address.id, shipping: true })}
                  >
                    Teslimatta varsayılan yap
                  </button>
                ) : null}

                {!address.isDefaultBilling ? (
                  <button
                    type="button"
                    className="btn-ghost btn-sm"
                    onClick={() => setDefault.mutate({ id: address.id, shipping: false })}
                  >
                    Faturada varsayılan yap
                  </button>
                ) : null}

                <button
                  type="button"
                  className="btn-ghost btn-sm ml-auto text-red-600"
                  onClick={() => setDeleteId(address.id)}
                >
                  Sil
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={editing !== null}
        title={editing?.id === null ? 'Yeni adres' : 'Adresi düzenle'}
        onClose={() => setEditing(null)}
        size="lg"
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => save.mutate()}
              disabled={save.isPending}
            >
              {save.isPending ? <Spinner className="h-4 w-4" /> : null}
              Kaydet
            </button>
          </>
        }
      >
        {editing ? (
          <div className="space-y-4">
            <FormErrors error={save.error} />

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Adres başlığı" htmlFor="addr-title" className="sm:col-span-2">
                <input
                  id="addr-title"
                  className="field"
                  placeholder="Ev, İş vb."
                  value={editing.form.title ?? ''}
                  onChange={(event) =>
                    setEditing((c) => c && { ...c, form: { ...c.form, title: event.target.value } })
                  }
                />
              </Field>

              {(
                [
                  ['firstName', 'Ad', true],
                  ['lastName', 'Soyad', true],
                  ['phone', 'Telefon', true],
                  ['company', 'Şirket', false],
                ] as const
              ).map(([key, label, required]) => (
                <Field key={key} label={label} htmlFor={`addr-${key}`} required={required}>
                  <input
                    id={`addr-${key}`}
                    className="field"
                    value={(editing.form[key] as string) ?? ''}
                    onChange={(event) =>
                      setEditing(
                        (c) => c && { ...c, form: { ...c.form, [key]: event.target.value } },
                      )
                    }
                  />
                </Field>
              ))}

              <Field label="İl" htmlFor="addr-city" required>
                {editing.form.countryCode === 'TR' ? (
                  <SearchableSelect
                    id="addr-city"
                    value={editing.form.city}
                    placeholder="İl Seçiniz..."
                    searchPlaceholder="İl adı veya plaka ara (örn: 34 veya İstanbul)..."
                    options={cities.map((c) => ({
                      value: c.name,
                      label: `${c.plate} - ${c.name}`,
                    }))}
                    onChange={(newCity) => {
                      setEditing((c) =>
                        c && {
                          ...c,
                          form: {
                            ...c.form,
                            city: newCity,
                            district: '',
                            addressLine2: '',
                          },
                        },
                      );
                    }}
                  />
                ) : (
                  <input
                    id="addr-city"
                    className="field"
                    value={editing.form.city}
                    onChange={(event) =>
                      setEditing((c) => c && { ...c, form: { ...c.form, city: event.target.value } })
                    }
                  />
                )}
              </Field>

              <Field label="İlçe" htmlFor="addr-district" required>
                {editing.form.countryCode === 'TR' ? (
                  <SearchableSelect
                    id="addr-district"
                    value={editing.form.district}
                    placeholder="İlçe Seçiniz..."
                    searchPlaceholder="İlçe ara (örn: Kadıköy)..."
                    options={districts.map((d) => ({
                      value: d,
                      label: d,
                    }))}
                    onChange={(newDistrict) =>
                      setEditing(
                        (c) =>
                          c && {
                            ...c,
                            form: {
                              ...c.form,
                              district: newDistrict,
                              addressLine2: '',
                            },
                          },
                      )
                    }
                    disabled={!editing.form.city}
                  />
                ) : (
                  <input
                    id="addr-district"
                    className="field"
                    value={editing.form.district}
                    onChange={(event) =>
                      setEditing((c) => c && { ...c, form: { ...c.form, district: event.target.value } })
                    }
                  />
                )}
              </Field>

              <Field label="Mahalle" htmlFor="addr-neighborhood">
                {editing.form.countryCode === 'TR' && neighborhoods.length > 0 ? (
                  <SearchableSelect
                    id="addr-neighborhood"
                    value={editing.form.addressLine2 ?? ''}
                    placeholder="Mahalle Seçiniz..."
                    searchPlaceholder="Mahalle ara..."
                    isLoading={loadingNeighborhoods}
                    options={neighborhoods}
                    onChange={(newNeighborhood) =>
                      setEditing(
                        (c) =>
                          c && {
                            ...c,
                            form: {
                              ...c.form,
                              addressLine2: newNeighborhood,
                            },
                          },
                      )
                    }
                    disabled={!editing.form.district}
                  />
                ) : (
                  <input
                    id="addr-line2"
                    className="field"
                    placeholder="Mahalle veya ek adres bilgisi"
                    value={editing.form.addressLine2 ?? ''}
                    onChange={(event) =>
                      setEditing(
                        (c) => c && { ...c, form: { ...c.form, addressLine2: event.target.value } },
                      )
                    }
                  />
                )}
              </Field>

              <Field label="Posta kodu" htmlFor="addr-postal">
                <input
                  id="addr-postal"
                  className="field"
                  placeholder="Örn: 34710"
                  value={editing.form.postalCode ?? ''}
                  onChange={(event) =>
                    setEditing(
                      (c) => c && { ...c, form: { ...c.form, postalCode: event.target.value } },
                    )
                  }
                />
              </Field>

              <Field label="Açık Adres (Cadde, Sokak, Kapı No, Daire)" htmlFor="addr-line1" required className="sm:col-span-2">
                <textarea
                  id="addr-line1"
                  rows={2}
                  className="field"
                  placeholder="Örn: İstiklal Cad. Çiçek Pasajı No: 12 Daire: 4"
                  value={editing.form.addressLine1}
                  onChange={(event) =>
                    setEditing(
                      (c) => c && { ...c, form: { ...c.form, addressLine1: event.target.value } },
                    )
                  }
                />
              </Field>

              <Field label="Ülke" htmlFor="addr-country" required>
                <select
                  id="addr-country"
                  className="field"
                  value={editing.form.countryCode}
                  onChange={(event) =>
                    setEditing(
                      (c) => c && { ...c, form: { ...c.form, countryCode: event.target.value } },
                    )
                  }
                >
                  <option value="TR">Türkiye</option>
                  <option value="DE">Almanya</option>
                  <option value="NL">Hollanda</option>
                  <option value="GB">Birleşik Krallık</option>
                  <option value="US">Amerika Birleşik Devletleri</option>
                </select>
              </Field>
            </div>

            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-ink-300 text-brand-600"
                checked={editing.form.isDefaultShipping}
                onChange={(event) =>
                  setEditing(
                    (c) => c && { ...c, form: { ...c.form, isDefaultShipping: event.target.checked } },
                  )
                }
              />
              Varsayılan teslimat adresim
            </label>

            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-ink-300 text-brand-600"
                checked={editing.form.isDefaultBilling}
                onChange={(event) =>
                  setEditing(
                    (c) => c && { ...c, form: { ...c.form, isDefaultBilling: event.target.checked } },
                  )
                }
              />
              Varsayılan fatura adresim
            </label>

            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-ink-300 text-brand-600"
                checked={editing.form.isCorporate}
                onChange={(event) =>
                  setEditing(
                    (c) => c && { ...c, form: { ...c.form, isCorporate: event.target.checked } },
                  )
                }
              />
              Kurumsal fatura
            </label>

            {editing.form.isCorporate ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Vergi dairesi" htmlFor="addr-taxoffice">
                  <input
                    id="addr-taxoffice"
                    className="field"
                    value={editing.form.taxOffice ?? ''}
                    onChange={(event) =>
                      setEditing(
                        (c) => c && { ...c, form: { ...c.form, taxOffice: event.target.value } },
                      )
                    }
                  />
                </Field>
                <Field label="Vergi numarası" htmlFor="addr-taxnumber">
                  <input
                    id="addr-taxnumber"
                    className="field"
                    value={editing.form.taxNumber ?? ''}
                    onChange={(event) =>
                      setEditing(
                        (c) => c && { ...c, form: { ...c.form, taxNumber: event.target.value } },
                      )
                    }
                  />
                </Field>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={deleteId !== null}
        title="Adresi sil"
        message="Bu adres kalıcı olarak silinecek."
        confirmLabel="Sil"
        danger
        busy={remove.isPending}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => deleteId !== null && remove.mutate(deleteId)}
      />
    </div>
  );
}

/* ------------------------------ Favoriler ------------------------------ */

export function WishlistPage() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const wishlist = useQuery({
    queryKey: ['account', 'wishlist'],
    queryFn: () => accountApi.wishlist(),
  });

  const remove = useMutation({
    mutationFn: (productId: number) => accountApi.removeFromWishlist(productId),
    onSuccess: () => {
      toast.success('Favorilerden çıkarıldı.');
      void queryClient.invalidateQueries({ queryKey: ['account', 'wishlist'] });
      void queryClient.invalidateQueries({ queryKey: ['account', 'wishlist-ids'] });
    },
    onError: (error) => toast.fromError(error, 'İşlem tamamlanamadı.'),
  });

  const updateNotify = useMutation({
    mutationFn: ({
      productId,
      notifyOnRestock,
      notifyOnPriceDrop,
    }: {
      productId: number;
      notifyOnRestock?: boolean;
      notifyOnPriceDrop?: boolean;
    }) => accountApi.updateWishlistNotifications(productId, notifyOnRestock, notifyOnPriceDrop),
    onSuccess: () => {
      toast.success('Bildirim tercihi kaydedildi.');
      void queryClient.invalidateQueries({ queryKey: ['account', 'wishlist'] });
    },
    onError: (err) => toast.fromError(err, 'Tercih kaydedilemedi.'),
  });

  if (wishlist.isLoading) return <PageLoader />;
  if (wishlist.isError)
    return <ErrorState error={wishlist.error} onRetry={() => void wishlist.refetch()} />;

  const data = wishlist.data!;

  if (data.length === 0) {
    return (
      <EmptyState
        title="Favori listeniz boş"
        description="Beğendiğiniz ürünleri favorilerinize ekleyerek sonra kolayca bulabilirsiniz."
        action={
          <Link to="/urunler" className="btn-primary">
            Ürünlere göz at
          </Link>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-medium text-ink-900">Favorilerim ({data.length} ürün)</h2>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {data.map((product) => {
          const hasPriceDrop =
            product.priceWhenAdded !== null &&
            product.priceWhenAdded !== undefined &&
            product.priceWhenAdded > product.minPrice;

          return (
            <div
              key={product.id}
              className="flex flex-col rounded-xl border border-ink-200 bg-white p-2 shadow-xs transition hover:shadow-md"
            >
              <div className="relative">
                <ProductCard product={product} />
              </div>

              {hasPriceDrop ? (
                <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800">
                  🎉 Fiyatı{' '}
                  {money(product.priceWhenAdded! - product.minPrice, product.currencyCode)} düştü!
                  <span className="block font-normal text-emerald-700">
                    Eski Fiyat: {money(product.priceWhenAdded!, product.currencyCode)} → Yeni:{' '}
                    {money(product.minPrice, product.currencyCode)}
                  </span>
                </div>
              ) : null}

              {/* Bildirim Tercihleri */}
              <div className="mt-3 flex flex-col gap-2 rounded-lg border border-ink-100 bg-ink-50/50 p-2.5 text-xs">
                <span className="font-semibold text-ink-800">Bildirim Ayarları:</span>
                <label className="flex cursor-pointer items-center gap-2 text-ink-700">
                  <input
                    type="checkbox"
                    checked={product.notifyOnPriceDrop ?? true}
                    onChange={(e) =>
                      updateNotify.mutate({
                        productId: product.id,
                        notifyOnPriceDrop: e.target.checked,
                      })
                    }
                    className="rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span>📉 Fiyat düşünce haber ver</span>
                </label>

                <label className="flex cursor-pointer items-center gap-2 text-ink-700">
                  <input
                    type="checkbox"
                    checked={product.notifyOnRestock ?? true}
                    onChange={(e) =>
                      updateNotify.mutate({
                        productId: product.id,
                        notifyOnRestock: e.target.checked,
                      })
                    }
                    className="rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span>🔔 Stok gelince haber ver</span>
                </label>
              </div>

              <div className="mt-3 border-t border-ink-100 pt-2">
                <button
                  type="button"
                  onClick={() => remove.mutate(product.id)}
                  disabled={remove.isPending}
                  className="w-full text-center text-xs font-medium text-red-600 hover:text-red-700"
                >
                  Listeden Çıkar
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Siparis tamamlandi ekrani. */
export function OrderSuccessPage() {
  const { publicId = '' } = useParams();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  // Checkout yaniti yonlendirmeyle tasinir. Misafir musteri siparis
  // ucundan veri cekemez (ve cekmemeli), bu yuzden ozet buradan gelir.
  const checkoutResult = location.state as CheckoutResultDto | null;

  const order = useQuery({
    queryKey: ['account', 'order', publicId],
    queryFn: () => accountApi.myOrder(publicId),
    enabled: publicId.length > 0 && isAuthenticated,
    retry: false,
  });

  // PayTR 3D Secure sonrasi harici yonlendirmede state bos gelecegi icin
  // anonim odeme durum sorgusu yapilir.
  const paymentStatus = useQuery({
    queryKey: ['payment-status', publicId],
    queryFn: () => paymentsApi.getStatus(publicId),
    enabled: publicId.length > 0 && !isAuthenticated && !checkoutResult,
    retry: 2,
  });

  const statusData = paymentStatus.data as
    | {
        orderNumber?: string;
        grandTotal?: number;
        currencyCode?: string;
        paidAt?: string | null;
      }
    | undefined;

  const summary = order.data
    ? {
        orderNumber: order.data.orderNumber,
        amount: order.data.grandTotal,
        currency: order.data.currencyCode,
        createdAt: order.data.createdAt as string | null,
      }
    : checkoutResult
      ? {
          orderNumber: checkoutResult.orderNumber,
          amount: checkoutResult.amountDue,
          currency: checkoutResult.currencyCode,
          createdAt: null,
        }
      : statusData && statusData.orderNumber
        ? {
            orderNumber: statusData.orderNumber,
            amount: statusData.grandTotal ?? 0,
            currency: statusData.currencyCode ?? 'TRY',
            createdAt: statusData.paidAt ?? null,
          }
        : null;

  // Analytics: Purchase event
  useEffect(() => {
    if (summary) {
      trackPurchase({
        orderNumber: summary.orderNumber,
        total: summary.amount,
        items:
          order.data?.lines?.map((line) => ({
            id: line.productId ?? line.id,
            name: line.productTitle,
            price: line.unitPrice,
            quantity: line.quantity,
            variant: line.variantTitle || undefined,
          })) || [],
      });
    }
  }, [summary?.orderNumber]);

  return (
    <div className="container-page py-16">
      <div className="mx-auto max-w-lg text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
          <svg viewBox="0 0 24 24" className="h-7 w-7 text-emerald-700" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12.5l4.5 4.5L19 7.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        <h1 className="mt-5 text-2xl font-semibold text-ink-900">Siparişiniz alındı</h1>
        <p className="mt-2 text-sm text-ink-600">
          Sipariş onayı e-posta adresinize gönderildi. Siparişinizi hesabınızdan takip edebilirsiniz.
        </p>

        {summary ? (
          <div className="card mt-6 p-5 text-left">
            <div className="flex items-center justify-between">
              <span className="text-sm text-ink-500">Sipariş numarası</span>
              <span className="font-semibold text-ink-900">{summary.orderNumber}</span>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <span className="text-sm text-ink-500">Tutar</span>
              <span className="font-semibold text-ink-900">
                {money(summary.amount, summary.currency)}
              </span>
            </div>
            {summary.createdAt ? (
              <div className="mt-2 flex items-center justify-between">
                <span className="text-sm text-ink-500">Tarih</span>
                <span className="text-sm text-ink-700">{formatDateTime(summary.createdAt)}</span>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {isAuthenticated ? (
            <Link to={`/hesabim/siparisler/${publicId}`} className="btn-primary">
              Siparişi görüntüle
            </Link>
          ) : (
            <Link to="/siparis-takip" className="btn-primary">
              Siparişimi takip et
            </Link>
          )}
          <Link to="/urunler" className="btn-secondary">
            Alışverişe devam et
          </Link>
        </div>

        {!isAuthenticated ? (
          <p className="mt-4 text-xs text-ink-500">
            Sipariş numaranız ve e-posta adresinizle siparişinizi takip edebilirsiniz.
          </p>
        ) : null}
      </div>
    </div>
  );
}
